import React, {useEffect, useRef} from 'react';
import {
  easeInOut,
  positionAtProgress,
  bearing,
  makeTruckEl,
  makeDestEl,
} from '../../helpers/map.helper';

function animateTruckMove(marker, imgEl, fromLngLat, toLngLat, duration) {
  let rafId = null;
  let startTs = null;
  const coords = [fromLngLat, toLngLat];

  function frame(ts) {
    if (!startTs) startTs = ts;
    const t = Math.min((ts - startTs) / duration, 1);
    const progress = easeInOut(t);
    const {lngLat, deg} = positionAtProgress(coords, progress);
    marker.setLngLat(lngLat);
    imgEl.style.transform = `rotate(${deg - 90}deg)`;
    if (t < 1) rafId = requestAnimationFrame(frame);
  }

  rafId = requestAnimationFrame(frame);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
  };
}

function loadMaplibre(cb) {
  if (window.maplibregl) {
    cb(window.maplibregl);
    return;
  }

  const existing = document.querySelector('script[data-maplibre]');
  if (existing) {
    existing.addEventListener('load', () => cb(window.maplibregl));
    return;
  }

  // Inject CSS
  if (!document.querySelector('link[data-maplibre]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.setAttribute('data-maplibre', '1');
    link.href = '/maplibre-gl.css';
    document.head.appendChild(link);
  }

  // Inject JS
  const script = document.createElement('script');
  script.setAttribute('data-maplibre', '1');
  script.src = '/maplibre-gl.js';
  script.onload = () => cb(window.maplibregl);
  document.head.appendChild(script);
}

const TrackingMap = ({
  driverLat,
  driverLng,
  driverLabel,
  destLat,
  destLng,
  destLabel,
  dropsBeforeYou,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const truckMarkerRef = useRef(null);
  const truckImgRef = useRef(null);
  const stopAnimRef = useRef(null);
  // Always-fresh driver props for async loadMaplibre callbacks
  const driverPropsRef = useRef(null);
  // Last driver position written after Effect 1 places the truck, or after
  // Effect 2 confirms a valid position. Only written with non-null, non-zero coords.
  const prevLatRef = useRef(null);
  const prevLngRef = useRef(null);

  const hasDest = destLat != null && destLng != null;
  const tooFarAway = dropsBeforeYou != null && dropsBeforeYou >= 4;
  const hasDriver =
    !tooFarAway &&
    driverLat != null &&
    driverLng != null &&
    driverLat !== 0 &&
    driverLng !== 0;

  driverPropsRef.current = {driverLat, driverLng, hasDriver, driverLabel};

  // Effect 1: Initialize map when destination or driver availability changes.
  // Does NOT depend on driverLat/driverLng — position updates are handled by Effect 2.
  useEffect(() => {
    if (!containerRef.current || !hasDest) return;

    let destroyed = false;

    if (stopAnimRef.current) {
      stopAnimRef.current();
      stopAnimRef.current = null;
    }
    truckMarkerRef.current = null;
    truckImgRef.current = null;
    // Reset prev-pos refs so Effect 2 skips the first position it sees after
    // map reinit (the truck will be placed at that position, not animated to it).
    prevLatRef.current = null;
    prevLngRef.current = null;

    loadMaplibre(maplibregl => {
      if (destroyed || !containerRef.current) return;

      // Read the latest driver props at the time the map actually loads
      const {
        driverLat: lat,
        driverLng: lng,
        hasDriver: hd,
        driverLabel: dlabel,
      } = driverPropsRef.current;

      const map = new maplibregl.Map({
        container: containerRef.current,
        // Vector GL style rather than the raster light_all/{z}/{x}/{y}.png tiles: CARTO
        // started requiring an API key for the raster endpoint on 2026-09-23 (keyless
        // requests now come back watermarked "API KEY REQUIRED"), but this hosted style.json
        // was still serving cleanly, keyless, as of the same date. Attribution is baked into
        // the style itself, so it no longer needs setting here.
        style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
        center: hd ? [lng, lat] : [destLng, destLat],
        zoom: 13,
        scrollZoom: false,
      });
      mapRef.current = map;
      map.addControl(new maplibregl.NavigationControl(), 'top-right');

      // Add destination marker immediately — HTML markers don't need tiles loaded
      new maplibregl.Marker({element: makeDestEl()})
        .setLngLat([destLng, destLat])
        .setPopup(
          new maplibregl.Popup({offset: 12}).setText(destLabel || 'Destination')
        )
        .addTo(map);

      if (hd) {
        // Add truck marker immediately — no need to wait for map.on('load')
        const {wrapper, img: imgEl} = makeTruckEl();
        const initialDeg = bearing([lng, lat], [destLng, destLat]);
        imgEl.style.transform = `rotate(${initialDeg - 90}deg)`;
        const truckMarker = new maplibregl.Marker({
          element: wrapper,
          anchor: 'center',
        })
          .setLngLat([lng, lat])
          .setPopup(
            new maplibregl.Popup({offset: 12}).setText(dlabel || 'Driver')
          )
          .addTo(map);

        truckMarkerRef.current = truckMarker;
        truckImgRef.current = imgEl;
        prevLatRef.current = lat;
        prevLngRef.current = lng;
      }

      // Route line and fitBounds require the map to be fully loaded
      map.on('load', () => {
        if (destroyed) return;

        if (!hd) {
          map.setZoom(14);
          return;
        }

        const bounds = new maplibregl.LngLatBounds();
        bounds.extend([lng, lat]);
        bounds.extend([destLng, destLat]);
        map.fitBounds(bounds, {padding: 60, maxZoom: 15, animate: false});

        const straightCoords = [
          [lng, lat],
          [destLng, destLat],
        ];
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: straightCoords},
          },
        });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': '#0000FF', 'line-width': 5, 'line-opacity': 0},
        });

        fetch(
          `https://router.project-osrm.org/route/v1/driving/${lng},${lat};${destLng},${destLat}?overview=full&geometries=geojson`
        )
          .then(r => r.json())
          .then(data => {
            if (!mapRef.current || destroyed) return;
            map
              .getSource('route')
              .setData({type: 'Feature', geometry: data.routes[0].geometry});
          })
          .catch(() => {});
      });
    });

    return () => {
      destroyed = true;
      if (stopAnimRef.current) {
        stopAnimRef.current();
        stopAnimRef.current = null;
      }
      prevLatRef.current = null;
      prevLngRef.current = null;
      truckMarkerRef.current = null;
      truckImgRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [hasDest, hasDriver, destLat, destLng]); // eslint-disable-line

  // Effect 2: Animate truck only when driver lat/lng changes to a new value.
  useEffect(() => {
    const prevLat = prevLatRef.current;
    const prevLng = prevLngRef.current;

    // Only update prevLatRef with valid coords so we never animate from 0,0
    prevLatRef.current = driverLat;
    prevLngRef.current = driverLng;

    // First position seen after mount/reinit — truck was already placed at
    // this position by Effect 1, nothing to animate.
    if (prevLat == null || prevLng == null) {
      console.log('[TrackingMap] effect2 skip: prevLat/prevLng null');
      return;
    }

    // Position hasn't actually changed
    if (prevLat === driverLat && prevLng === driverLng) {
      console.log('[TrackingMap] effect2 skip: position unchanged');
      return;
    }

    const marker = truckMarkerRef.current;
    const imgEl = truckImgRef.current;
    // Map still loading — skip; truck will be placed at the latest position
    // once map.on('load') fires and syncs prevLatRef/prevLngRef.
    if (!marker || !imgEl) {
      console.log('[TrackingMap] effect2 skip: marker not ready');
      return;
    }

    if (stopAnimRef.current) stopAnimRef.current();

    stopAnimRef.current = animateTruckMove(
      marker,
      imgEl,
      [prevLng, prevLat],
      [driverLng, driverLat],
      8000
    );
  }, [driverLat, driverLng]); // eslint-disable-line

  if (!hasDest) return null;

  return (
    <div
      ref={containerRef}
      style={{width: '100%', height: '350px', margin: '1.5rem 0'}}
    />
  );
};

export default TrackingMap;
